import {PullRequestDiff, type PullRequestDiffProps} from './PullRequestDiff'

import {PullRequestDiffEntryErrorFallback} from './PullRequestDiffEntryErrorFallback'
import {memo, useEffect, useRef} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {
  useDiffEntry,
  useDiffSummary,
  useProgressiveDiffEntryByPathDigest,
  useIsSingleFileMode,
  useSelectedPathDigest,
} from '../stores/selectors'
import {DiffFileHeader} from '@github-ui/diff-file-header'
import {DiffPlaceholder} from '@github-ui/diffs/DiffParts'
import {noop} from '@github-ui/noop'
import styles from '@github-ui/diff-lines/Diff.module.css'
import {clsx} from 'clsx'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import type {ScrollToAndFocusFunction} from '../hooks/use-scroll-to-and-focus'
import {LazyDiffEntryLoadingSkeleton} from './progressive-diff/LazyDiffEntryLoadingSkeleton'

export type SingleFileModePullRequestDiffEntryProps = {
  diffEntryPathDigest: string
  filteredDiffSummaries: PullRequestFileTreeDiff[]
  onScrollToAndFocusEntry: ScrollToAndFocusFunction
} & Pick<
  PullRequestDiffProps,
  | 'diffManuallyExpanded'
  | 'headBranchName'
  | 'headRepoName'
  | 'headRepoOwner'
  | 'isSelected'
  | 'pullRequestGlobalRelayId'
  | 'repository'
>

export const SingleFileModePullRequestDiffEntry = memo(function SingleFileModePullRequestDiffEntry({
  diffEntryPathDigest,
  diffManuallyExpanded,
  filteredDiffSummaries,
  headBranchName,
  headRepoName,
  headRepoOwner,
  pullRequestGlobalRelayId,
  repository,
  onScrollToAndFocusEntry,
}: SingleFileModePullRequestDiffEntryProps) {
  const selectedPathDigest = useSelectedPathDigest() || diffEntryPathDigest
  const progressiveDiffEntry = useProgressiveDiffEntryByPathDigest(selectedPathDigest)
  const diffSummary = useDiffSummary(progressiveDiffEntry?.path || '')
  const diffEntryPath = progressiveDiffEntry?.path || ''
  const currentDiffEntry = useDiffEntry(diffEntryPath)
  const isInSingleFileMode = useIsSingleFileMode()

  /**
   * Scroll to and focus the selected lines on first render
   *
   * Without the skipScrollToAndFocus ref, this effect would run whenever someone clicks on a line cell, which updates
   * the hash fragment, which in turn updates the selectedFullDiffHash and triggers a re-render.
   * Running the effect all the time results in jumpy behavior while viewing the diff.
   *
   * We should move away from listening to hash changes, see: https://github.com/github/pull-requests/issues/18004
   */
  const skipScrollToAndFocus = useRef(false)
  useEffect(() => {
    if (skipScrollToAndFocus.current) return
    if (progressiveDiffEntry?.isLoaded) {
      onScrollToAndFocusEntry(selectedPathDigest, {focusPathName: true})
      skipScrollToAndFocus.current = true
    }
  }, [onScrollToAndFocusEntry, progressiveDiffEntry?.isLoaded, selectedPathDigest])

  const matchesFilter = filteredDiffSummaries.some(ds => ds.path === diffEntryPath)
  if (!matchesFilter) {
    return <FilesChangedFilterBlankSlate />
  }

  if (progressiveDiffEntry && !progressiveDiffEntry.isLoaded) {
    const approximateLineCount = 5

    return (
      <div
        role="region"
        id={`diff-${diffEntryPath}`}
        className={clsx(styles.diffTargetable, styles.diff)}
        data-targeted={false}
        key={`${selectedPathDigest}_${diffEntryPath}`}
        data-hpc
      >
        <div className={styles.diffHeaderWrapper}>
          <DiffFileHeader
            areLinesExpanded={false}
            canExpandOrCollapseLines={false}
            fileLinkHref={`#diff-${selectedPathDigest}`}
            canToggleRichDiff={false}
            linesAdded={diffSummary?.linesAdded ?? 0}
            linesChanged={diffSummary?.linesChanged ?? 0}
            linesDeleted={diffSummary?.linesDeleted ?? 0}
            newPath={diffEntryPath}
            patchStatus={''}
            path={diffEntryPath}
            onToggleExpandAllLines={noop}
            onToggleFileCollapsed={noop}
            onToggleDiffDisplay={noop}
            isInSingleFileMode={isInSingleFileMode}
          />
        </div>
        <LazyDiffEntryLoadingSkeleton
          progressiveDiffEntry={progressiveDiffEntry}
          approximateLineCount={approximateLineCount}
        />
      </div>
    )
  }
  if (!currentDiffEntry) {
    return (
      <PullRequestDiffEntryErrorFallback
        linesAdded={0}
        linesChanged={0}
        linesDeleted={0}
        path={diffEntryPath}
        pathDigest={diffEntryPathDigest}
        newTreeEntry={undefined}
        oldTreeEntry={undefined}
        status={'MODIFIED'}
      />
    )
  }

  return (
    <ErrorBoundary
      boundaryName="SingleFileModePullRequestDiffEntry"
      fallback={<PullRequestDiffEntryErrorFallback {...currentDiffEntry} />}
    >
      <PullRequestDiff
        changeType={currentDiffEntry.status}
        diffLines={currentDiffEntry.diffLines}
        diffManuallyExpanded={diffManuallyExpanded}
        diffSize={currentDiffEntry.diffSize}
        headBranchName={headBranchName}
        headRepoName={headRepoName}
        headRepoOwner={headRepoOwner}
        helpUrl={currentDiffEntry.helpUrl}
        isBinary={currentDiffEntry.isBinary}
        isSelected={false}
        isTooBig={currentDiffEntry.isTooBig}
        linesAdded={currentDiffEntry.linesAdded}
        linesChanged={currentDiffEntry.linesChanged}
        linesDeleted={currentDiffEntry.linesDeleted}
        newCommitOid={currentDiffEntry.newCommitOid}
        newTreeEntry={currentDiffEntry.newTreeEntry}
        objectId={currentDiffEntry.objectId}
        oldCommitOid={currentDiffEntry.oldCommitOid}
        oldTreeEntry={currentDiffEntry.oldTreeEntry}
        path={currentDiffEntry.path}
        pathDigest={currentDiffEntry.pathDigest}
        pullRequestGlobalRelayId={pullRequestGlobalRelayId}
        repository={repository}
        reviewed={currentDiffEntry.reviewed ?? false}
        richDiff={currentDiffEntry.richDiff}
        status={currentDiffEntry.status}
        submodule={currentDiffEntry.submodule}
        truncatedReason={currentDiffEntry.truncatedReason}
      />
      {/* DiffPlaceholder is used for the skeleton placeholder when a diff isn't loaded.
            It needs to be somewhere on the page so that it can be drawn from within the diff lines component. */}
      <DiffPlaceholder />
    </ErrorBoundary>
  )
})

try{ SingleFileModePullRequestDiffEntry.displayName ||= 'SingleFileModePullRequestDiffEntry' } catch {}